import React, { useState, useEffect } from "react";
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionActions,
  TextField,
  Button,
  Container,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import CardComponent from "../../common/components/CardComponent";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowCircleLeftOutlinedIcon from "@mui/icons-material/ArrowCircleLeftOutlined";
import { Edit, Add, Save } from "@mui/icons-material";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import DatePickerComponent from "../../common/components/DatePickerComponent.jsx";
import {
  dqidOptions,
  extEligibilityOptions,
  aiOptions,
  channelOptions,
  msgtimeOptions,
  repeatOptions,
} from "../../common/constants/scheduleDataConstants";
import EditCampaignModal from "./modals/EditCampaignModal.jsx";
import VariableModal from "./modals/VariableModal.jsx";
import AddScheduleModal from "./modals/AddScheduleModal.jsx";
import DeleteScheduleDailog from "./modals/DeleteScheduleDailog.jsx";
import AddLibraryModal from "./modals/AddLibraryModal.jsx";

import {
  getschedule,
  saveSchedule,
  addSchedule,
  deleteSchedule,
} from "../../services/schedule";

import { updateCampaign } from "../../services/campaign";
import ProgressOverlay from "../../common/components/ProgressOverlay.jsx";
import { pstDateToUtc, utcToPstDate, getTimeZoneFullName, dateToUtcByTz, utcToDateByTz } from "../../utils/utils.js";
import { SCHEDULE_STRINGS } from "../../common/constants/scheduleModule/scheduleConstants.js";
import { getClientConfig } from "../../services/commonService";



const Schedule = ({ campaignID, onBack }) => {
  const [campaignDetails, setCampaignDetails] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isVariableModalOpen, setIsVariableModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddScheduleModalOpen, setIsAddScheduleModalOpen] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isAddLibraryOpen, setIsAddLibraryOpen] = useState(false);
  const [libraryRows, setLibraryRows] = useState([]);
  const [clientTimezone, setClientTimezone] = useState("");
  const [clientTimezoneFull, setClientTimezoneFull] = useState("");
  const [newSchedule] = useState({
    name: "",
    startdate: "",
    enddate: "",
    dqid: "",
    exteligibilityid: "",
    repeatid: "",
    textmsg: "",
  });
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const userId = localStorage.getItem("userId") || "";

  useEffect(() => {
    const fetchClientConfig = async () => {
      try {
        const data = await getClientConfig();
        if (data && data.length > 0 && data[0].clienttimezone) {
          setClientTimezone(data[0].clienttimezone);
          setClientTimezoneFull(getTimeZoneFullName(data[0].clienttimezone));
        }
      } catch (error) {
        console.error("Error fetching client config:", error);
      }
    };
    fetchClientConfig();
  }, []);

  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        setIsLoading(true);
        const data = await getschedule(campaignID);
        if (data && data.length > 0) {
          const campaign = data[0];

          setCampaignDetails({
            name: campaign.CampaignName,
            description: campaign.CampaignDescription,
            campaigntypeid: campaign.CampaignType,
            useAI: campaign.useai,
            category: campaign.CampaignCategory,   // add
            language: campaign.CampaignLanguage,   // add
          });
        }

        // Convert UTC dates to PST format for display
        const convertedData = (data || []).map(schedule => ({
          ...schedule,
          startdate: schedule.startdate ? utcToDateByTz(schedule.startdate, clientTimezone) : schedule.startdate,
          enddate: schedule.enddate ? utcToDateByTz(schedule.enddate, clientTimezone) : schedule.enddate,
        }));

        setSchedules(convertedData);
      } catch (error) {
        console.error("Error fetching schedules:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (campaignID) {
      fetchSchedules();
    }
  }, [campaignID]);

  // Open edit modal
  const handleEditClick = () => {
    setIsEditModalOpen(true);
  };

  // Save campaign details
  const handleSaveCampaign = async (updatedCampaign) => {
    try {
      await updateCampaign(campaignID, updatedCampaign);
      setCampaignDetails(updatedCampaign);
      setIsEditModalOpen(false);
      setSuccessMessage("Campaign updated successfully!");

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error updating campaign:", error);
    }
  };

  // Handle schedule field changes
  const handleScheduleChange = (index, field, value) => {
    const updatedSchedules = [...schedules];
    updatedSchedules[index] = { ...updatedSchedules[index], [field]: value };
    setSchedules(updatedSchedules);
  };

  // Save a specific schedule entry

  const handleSaveSchedule = async (schedule) => {
    setIsLoading(true);
    try {
      // Only name is required
      if (!schedule.name) {
        console.error("Name is required.");
        return;
      }

      const dateFields = ["startdate", "enddate"];
      dateFields.forEach((f) => {
        if (schedule[f] instanceof Date && !isNaN(schedule[f])) {
          const d = schedule[f];
          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          const formattedDate = `${yyyy}-${mm}-${dd}`;
          schedule[f] = dateToUtcByTz(formattedDate, clientTimezone);
        }
      });

      // Prepare request body (same as before)
      const requestBody = {
        campaignID: campaignID, // Assuming you have a campaignID variable
        scheduleID: schedule.id, // Assuming each schedule has an ID for updating
        name: schedule.name,
        startdate: schedule.startdate || null,
        enddate: schedule.enddate || null,
        channeltypeid: schedule.channeltypeid || null,
        dqid: schedule.dqid || null,
        exteligibilityid: schedule.exteligibilityid || null,
        repeatid: schedule.repeatid || null,
        useai: schedule.useai || false,
        textmsg: schedule.textmsg || "",
        montime: schedule.montime || null,
        tuetime: schedule.tuetime || null,
        wedtime: schedule.wedtime || null,
        thutime: schedule.thutime || null,
        fritime: schedule.fritime || null,
        sattime: schedule.sattime || null,
        suntime: schedule.suntime || null,
        // Percentages split by day
        monmsg: schedule.monmsg || 0,
        tuemsg: schedule.tuemsg || 0,
        wedmsg: schedule.wedmsg || 0,
        thumsg: schedule.thumsg || 0,
        frimsg: schedule.frimsg || 0,
        satmsg: schedule.satmsg || 0,
        sunmsg: schedule.sunmsg || 0,
        userId: Number(userId),
      };

      // Call saveSchedule from the imported API service
      const response = await saveSchedule(requestBody); // Assuming saveSchedule handles the API call

      console.log("Schedule saved successfully:", response);
      setSuccessMessage("Schedule saved successfully!");

      // Refresh the schedules list after saving
      const updatedSchedules = await getschedule(campaignID);
      setSchedules(updatedSchedules);

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error(
        "Error saving schedule:",
        error.response ? error.response.data : error.message
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Delete a schedule
  const handleDeleteSchedule = (schedule) => {
    if (!schedule || !schedule.id) return;
    setDeleteTarget(schedule);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !deleteTarget.id) return;
    setIsLoading(true);
    try {
      setOpenDeleteDialog(false);
      await deleteSchedule(deleteTarget.id);
      setSuccessMessage("Schedule deleted successfully!");
      const updatedSchedules = await getschedule(campaignID);
      setSchedules(updatedSchedules);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error deleting schedule:", error);
    } finally {
      setIsLoading(false);
      setOpenDeleteDialog(false);
      setDeleteTarget(null);
    }
  };

  // Save a new schedule
  const handleSaveNewSchedule = async (schedule) => {
    setIsLoading(true);
    try {
      if (!schedule.name) {
        console.error("Title is required");
        return;
      }

      await addSchedule(campaignID, schedule.name); // Pass only title, not the whole object
      setSuccessMessage("New schedule added successfully!");
      setIsAddScheduleModalOpen(false);

      // Refresh schedules
      const updatedSchedules = await getschedule(campaignID);
      setSchedules(updatedSchedules);

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error adding schedule:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInsertDataVariables = (schedule) => {
    setIsVariableModalOpen(true);
  };

  const handleAddLibrary = () => {
    setIsAddLibraryOpen(true);
  };

  return (
    <Container maxWidth={false} disableGutters>
      {isLoading && <ProgressOverlay />}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      <Grid container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
        <Grid size="auto">
          <IconButton variant="outlined" color="secondary" onClick={onBack}>
            <ArrowCircleLeftOutlinedIcon />
          </IconButton>
        </Grid>
        <Grid size="grow">
          <Typography variant="h6">
            {campaignDetails && campaignDetails.name}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2} disableGutters>
        <Grid size={{ xs: 9 }}>
          <CardComponent
            title={SCHEDULE_STRINGS.SCHEDULES}
            hasContentBg={true}
            actions={
              campaignDetails ? (
                <IconButton onClick={() => setIsAddScheduleModalOpen(true)}>
                  <Add />
                </IconButton>
              ) : (
                ""
              )
            }
          >
            {/* Schedule Records */}
            {schedules.map((schedule, index) => (
              <Accordion key={index}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    {schedule.name || SCHEDULE_STRINGS.UNNAMED_SCHEDULE}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {/* First Row: Name, Start Date, End Date */}
                  <Grid container spacing={2} fullWidth>
                    <Grid size={{ xs: 4 }}>
                      <TextField
                        label={SCHEDULE_STRINGS.TITLE}
                        fullWidth
                        value={schedule.name}
                        size="small"
                        onChange={(e) =>
                          handleScheduleChange(index, "name", e.target.value)
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 3 }}>
                      <DatePickerComponent
                        label={clientTimezone ? `${SCHEDULE_STRINGS.START_DATE} - ${clientTimezone}` : SCHEDULE_STRINGS.START_DATE}
                        date={schedule.startdate}
                        onDateChange={(e) =>
                          handleScheduleChange(index, "startdate", e)
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 3 }}>
                      <DatePickerComponent
                        label={clientTimezone ? `${SCHEDULE_STRINGS.END_DATE} - ${clientTimezone}` : SCHEDULE_STRINGS.END_DATE}
                        date={schedule.enddate}
                        onDateChange={(e) =>
                          handleScheduleChange(index, "enddate", e)
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>
                          {SCHEDULE_STRINGS.PAST_DUE_DAYS}
                        </InputLabel>
                        <Select
                          value={schedule.dqid}
                          label={SCHEDULE_STRINGS.PAST_DUE_DAYS}
                          onChange={(e) =>
                            handleScheduleChange(index, "dqid", e.target.value)
                          }
                        >
                          {dqidOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {/* Second Row: DQ, Extension, Repeat */}
                    <Grid size={{ xs: 3 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{SCHEDULE_STRINGS.CHANNEL}</InputLabel>
                        <Select
                          value={schedule.channeltypeid}
                          label={SCHEDULE_STRINGS.CHANNEL}
                          onChange={(e) =>
                            handleScheduleChange(
                              index,
                              "channeltypeid",
                              e.target.value
                            )
                          }
                        >
                          {channelOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 3 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>
                          {SCHEDULE_STRINGS.EXTENSION_ELIGIBILITY}
                        </InputLabel>
                        <Select
                          value={schedule.exteligibilityid}
                          label={SCHEDULE_STRINGS.EXTENSION_ELIGIBILITY}
                          onChange={(e) =>
                            handleScheduleChange(
                              index,
                              "exteligibilityid",
                              e.target.value
                            )
                          }
                        >
                          {extEligibilityOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 3 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{SCHEDULE_STRINGS.RECURRING}</InputLabel>
                        <Select
                          value={schedule.repeatid}
                          label={SCHEDULE_STRINGS.RECURRING}
                          onChange={(e) =>
                            handleScheduleChange(
                              index,
                              "repeatid",
                              e.target.value
                            )
                          }
                        >
                          {repeatOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 3 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{SCHEDULE_STRINGS.USE_AI}</InputLabel>
                        <Select
                          value={schedule.useai}
                          label={SCHEDULE_STRINGS.USE_AI}
                          onChange={(e) =>
                            handleScheduleChange(index, "useai", e.target.value)
                          }
                        >
                          {aiOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Typography variant="body2" color="primary" sx={{ my: 2 }}>
                    {SCHEDULE_STRINGS.PERCENTAGES_SPLIT}
                  </Typography>

                  {/* Third Row: Text Messages (Full Width, Min 5 Rows) */}

                  <Grid container spacing={2}>
                    {days.map((day) => (
                      <Grid key={day} size={1.5}>
                        <TextField
                          label={day}
                          fullWidth
                          size="small"
                          value={schedule[`${day.toLowerCase()}msg`] || ""}
                          onChange={(e) =>
                            handleScheduleChange(
                              index,
                              `${day.toLowerCase()}msg`,
                              e.target.value
                            )
                          }
                        />
                      </Grid>
                    ))}

                    {/* Single Grid for Total */}
                    <Grid size={1.5}>
                      <TextField
                        label={SCHEDULE_STRINGS.TOTAL}
                        fullWidth
                        size="small"
                        readOnly
                        disabled
                        value={days.reduce(
                          (sum, day) =>
                            sum +
                            (parseInt(schedule[`${day.toLowerCase()}msg`]) ||
                              0),
                          0
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Typography variant="body2" sx={{ my: 2 }} color="primary">
                    {clientTimezoneFull ? `${SCHEDULE_STRINGS.TIMES_BY_DAY} - ${clientTimezoneFull}` : SCHEDULE_STRINGS.TIMES_BY_DAY}
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid size={1.5}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Mon</InputLabel>
                        <Select
                          value={schedule.montime}
                          label="Mon"
                          onChange={(e) =>
                            handleScheduleChange(
                              index,
                              "montime",
                              e.target.value
                            )
                          }
                        >
                          {msgtimeOptions.map((option) => (
                            <MenuItem key={option.id} value={option.name}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={1.5}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Tue</InputLabel>
                        <Select
                          value={schedule.tuetime}
                          label="Tue"
                          onChange={(e) =>
                            handleScheduleChange(
                              index,
                              "tuetime",
                              e.target.value
                            )
                          }
                        >
                          {msgtimeOptions.map((option) => (
                            <MenuItem key={option.id} value={option.name}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={1.5}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Wed</InputLabel>
                        <Select
                          value={schedule.wedtime}
                          label="Wed"
                          onChange={(e) =>
                            handleScheduleChange(
                              index,
                              "wedtime",
                              e.target.value
                            )
                          }
                        >
                          {msgtimeOptions.map((option) => (
                            <MenuItem key={option.id} value={option.name}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={1.5}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Thu</InputLabel>
                        <Select
                          value={schedule.thutime}
                          label="Thu"
                          onChange={(e) =>
                            handleScheduleChange(
                              index,
                              "thutime",
                              e.target.value
                            )
                          }
                        >
                          {msgtimeOptions.map((option) => (
                            <MenuItem key={option.id} value={option.name}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={1.5}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Fri</InputLabel>
                        <Select
                          value={schedule.fritime}
                          label="Fri"
                          onChange={(e) =>
                            handleScheduleChange(
                              index,
                              "fritime",
                              e.target.value
                            )
                          }
                        >
                          {msgtimeOptions.map((option) => (
                            <MenuItem key={option.id} value={option.name}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={1.5}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Sat</InputLabel>
                        <Select
                          value={schedule.sattime}
                          label="Sat"
                          onChange={(e) =>
                            handleScheduleChange(
                              index,
                              "sattime",
                              e.target.value
                            )
                          }
                        >
                          {msgtimeOptions.map((option) => (
                            <MenuItem key={option.id} value={option.name}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={1.5}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Sun</InputLabel>
                        <Select
                          value={schedule.suntime}
                          label="Sun"
                          onChange={(e) =>
                            handleScheduleChange(
                              index,
                              "suntime",
                              e.target.value
                            )
                          }
                        >
                          {msgtimeOptions.map((option) => (
                            <MenuItem key={option.id} value={option.name}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  {/* Fourth Row: Text Messages (Full Width, Min 5 Rows) */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mt: 2,
                    }}
                  >
                    <Button
                      variant="text"
                      onClick={() => handleInsertDataVariables(schedule)}
                    >
                      {SCHEDULE_STRINGS.INSERT_DATA_VARIABLES}
                    </Button>
                    <Button
                      variant="outlined"
                      sx={{ mr: 0 }}
                      size="small"
                      onClick={handleAddLibrary}
                      startIcon={<LocalLibraryIcon />}
                    >
                      {SCHEDULE_STRINGS.ADD_LIBRARY}
                    </Button>
                  </Box>

                  <TextField
                    label={SCHEDULE_STRINGS.INITIAL_OUTBOUND_MESSAGE}
                    fullWidth
                    size="small"
                    multiline
                    minRows={4}
                    value={schedule.textmsg}
                    sx={{ mt: 2 }}
                    onChange={(e) =>
                      handleScheduleChange(index, "textmsg", e.target.value)
                    }
                  />
                </AccordionDetails>
                <AccordionActions
                  sx={{ backgroundColor: "#FAFAFA", pr: 2, py: 1 }}
                >
                  <Button
                    color="error"
                    variant="outlined"
                    sx={{ mr: 1 }}
                    size="small"
                    onClick={() => handleDeleteSchedule(schedule)}
                  >
                    {SCHEDULE_STRINGS.DELETE}
                  </Button>
                  <Button
                    variant="outlined"
                    sx={{ mr: 0 }}
                    size="small"
                    onClick={() => handleSaveSchedule(schedule)}
                    startIcon={<Save />}
                  >
                    {SCHEDULE_STRINGS.SAVE_SCHEDULES}
                  </Button>
                </AccordionActions>
              </Accordion>
            ))}
          </CardComponent>
        </Grid>
        <Grid size={{ xs: 3 }}>
          {/* Campaign Details */}
          {campaignDetails && (
            <CardComponent
              title={SCHEDULE_STRINGS.DESCRIPTION}
              actions={
                <IconButton onClick={handleEditClick}>
                  <Edit />
                </IconButton>
              }
            >
              {campaignDetails.description}
            </CardComponent>
          )}
        </Grid>
      </Grid>

      {/* Update Campaign Modal */}
      {isEditModalOpen && (
        <EditCampaignModal
          open={isEditModalOpen}
          campaign={campaignDetails}
          onSave={handleSaveCampaign}
          onClose={() => setIsEditModalOpen(false)}
        />
      )}
      {/* Add Schedule Modal */}
      {isAddScheduleModalOpen && (
        <AddScheduleModal
          open={isAddScheduleModalOpen}
          schedule={newSchedule}
          onSave={handleSaveNewSchedule}
          onClose={() => setIsAddScheduleModalOpen(false)}
        />
      )}

      {/* Insert new variable Modal */}
      {isVariableModalOpen && (
        <VariableModal
          open={isVariableModalOpen}
          onClose={() => setIsVariableModalOpen(false)}
        />
      )}

      {/* Delete schedule confirmation dialog */}
      <DeleteScheduleDailog
        open={openDeleteDialog}
        schedule={deleteTarget}
        onConfirm={confirmDelete}
        onClose={() => {
          setOpenDeleteDialog(false);
          setDeleteTarget(null);
        }}
      />
      {/* Add Library Modal */}
      {isAddLibraryOpen && (
        <AddLibraryModal
          open={isAddLibraryOpen}
          onClose={() => setIsAddLibraryOpen(false)}
          initialLibrary={libraryRows}
          onSave={(rows) => {
            setLibraryRows(rows || []);
            setIsAddLibraryOpen(false);
            setSuccessMessage("Library saved");
            setTimeout(() => setSuccessMessage(""), 3000);
          }}
        />
      )}
    </Container>
  );
};

export default Schedule;
